'use client';

import { useMemo, useState } from 'react';

import { Button, Card, Stack, Switch } from '@backyard-ui/core';

import { type CrossService } from '@/infra/services/catalog/crosses/get-crosses-services/model';
import { Image } from '@/presenters/components/common/Image';
import {
  ServiceContractInfoModal,
  ServiceContractConfirmModal,
} from '@/presenters/components/common/ServiceContractModal';
import { formatPrice } from '@/utils/price/formatPrice';

interface ServiceOfferCardProps {
  data: CrossService;
  isSelected?: boolean;
  onSelectionChange?: (isSelected: boolean) => void;
}

const WARRANTY_TERM_TITLE = 'Garantia do Serviço';

function ServiceOfferCard(props: Readonly<ServiceOfferCardProps>) {
  const {
    data: { price, acceptanceTerms },
    isSelected = false,
    onSelectionChange,
  } = props;

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSwitchSelected, setIsSwitchSelected] = useState(false);

  const handleServiceConfirm = (accepted: boolean) => {
    if (accepted) {
      onSelectionChange?.(true);
      setIsSwitchSelected(false);
    } else {
      setIsSwitchSelected(false);
    }
    setIsConfirmModalOpen(false);
  };

  const handleModalClose = (isOpen: boolean) => {
    if (!isOpen && isSwitchSelected) {
      setIsSwitchSelected(false);
    }

    setIsConfirmModalOpen(isOpen);
  };

  const warrantyText = useMemo(() => {
    const warranty = acceptanceTerms?.find(
      (term) => term.title === WARRANTY_TERM_TITLE
    );
    return warranty?.message.toLowerCase() ?? null;
  }, [acceptanceTerms]);

  const priceText = price ? formatPrice(price.to) : null;

  if (!warrantyText || !price || !acceptanceTerms?.length) return null;

  return (
    <>
      <Card.Root
        direction="row"
        UNSAFE_className="w-full"
        data-testid="service-offer-card"
      >
        <div className="flex aspect-[78/82] w-20 flex-shrink-0 items-center justify-center rounded-l-md bg-green-700 sm:w-24 md:w-24">
          <Image
            src="/images/catalog/product/service-logo.png"
            alt="Instalações e Reformas"
            width={66}
            height={74}
            className="h-auto w-3/4 object-contain sm:w-4/5"
          />
        </div>

        <Card.Body UNSAFE_className="flex items-center justify-center bg-gray-100">
          <Stack align="center" spacing={{ initial: '2', md: '4', lg: '2' }}>
            <div>
              <p className="text-sm md:text-lg">
                Instalação por <b className="whitespace-nowrap">{priceText}</b>{' '}
                à vista ou parcelado, com <b>{warrantyText}</b> de garantia.{' '}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsInfoModalOpen(true)}
                  UNSAFE_className="!h-auto !min-h-0 whitespace-nowrap !p-0 text-sm font-bold text-green-700 underline hover:no-underline md:text-lg"
                >
                  Saiba mais
                </Button>
              </p>
            </div>

            <div>
              <Switch
                aria-label="Incluir instalação do serviço"
                size="md"
                isChecked={isSelected || isSwitchSelected}
                onCheckedChange={() => {
                  if (!isSelected) {
                    setIsSwitchSelected(true);
                    setIsConfirmModalOpen(true);
                  } else {
                    onSelectionChange?.(false);
                  }
                }}
              />
            </div>
          </Stack>
        </Card.Body>
      </Card.Root>
      <ServiceContractInfoModal
        isOpen={isInfoModalOpen}
        onOpenChange={setIsInfoModalOpen}
        acceptanceTerms={acceptanceTerms}
      />

      <ServiceContractConfirmModal
        isOpen={isConfirmModalOpen}
        onOpenChange={handleModalClose}
        acceptanceTerms={acceptanceTerms}
        onConfirm={handleServiceConfirm}
      />
    </>
  );
}

export default ServiceOfferCard;
